// @i18n-ignore-file
import { useStatsigClient } from '@statsig/react-bindings'

import { useLocaleContext } from '@src/components/locale/locale.context'
import type { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import { getLocaleConfig, STATSIG_LOCALE_CONFIG_KEY } from '@src/config/localisation/statsig-locale-config-constants'
import type { StatsigLocaleDynamicConfig } from '@src/config/localisation/statsig-locale-config.types'

import { mergeDefaultIntoStatsigLocaleConfig } from './statsig-provider.utils'
import { useStatsigInitContext } from './statsig.context'

/**
 * Hook to access Statsig locale dynamic configuration (CSR)
 *
 * ⚠️ MERGE BEHAVIOUR:
 * 1. **STATSIG IS BASE** – Fetches from STATSIG_LOCALE_CONFIG_KEY; returned config starts from Statsig.
 * 2. **DEFAULT ONLY ADDS MISSING** – Keys not present in Statsig are added from default. If the key is present in Statsig, it is left as-is (including when value is undefined or null).
 * 3. **FALLBACK ON FAILURE** – If Statsig returns empty/null/undefined or throws, returns default config only.
 *
 * @param localeProp - Optional locale to override LocaleContext (useful before context is ready)
 * @returns Merged locale configuration (Statsig + default for missing keys only)
 */
export const useStatsigDynamicLocaleConfig = (localeProp?: TAbsoluteLocale): StatsigLocaleDynamicConfig => {
  const { getDynamicConfig } = useStatsigClient()
  const { init: isStatsigReady } = useStatsigInitContext()
  const { locale: contextLocale } = useLocaleContext()

  // Use passed prop if available (e.g. for components outside context/during hydration mismatch)
  const currentLocale = localeProp ?? contextLocale

  const defaultConfig = getLocaleConfig(currentLocale)

  // Return default config early if Statsig is still initializing (avoids empty config race condition)
  // to be decided default or empty
  if (!isStatsigReady) {
    return defaultConfig
  }

  try {
    const config = getDynamicConfig(STATSIG_LOCALE_CONFIG_KEY)
    const configValue = config.value as unknown as StatsigLocaleDynamicConfig | null | undefined

    if (!configValue || Object.keys(configValue).length === 0) {
      console.warn(`[Statsig CSR] Locale config not found in Statsig for ${currentLocale}, using default only`, {
        locale: currentLocale,
      })
      return defaultConfig
    }

    const merged = mergeDefaultIntoStatsigLocaleConfig(
      defaultConfig as unknown as Record<string, unknown>,
      configValue as unknown as Record<string, unknown>,
    ) as StatsigLocaleDynamicConfig

    return merged
  } catch (error) {
    console.error(`[Statsig CSR] Error fetching locale config for ${currentLocale}, using default:`, {
      locale: currentLocale,
      error,
    })
    return defaultConfig
  }
}
